// Shared modal shell for every new-hire onboarding overlay (Complete / View record / Edit plan).
// Mirrors the mockup's `.modal` (title row + divider, scrolling body, footer with Cancel then primary).
import React from 'react';
import { Modal, View, Text, ScrollView, Pressable, StyleSheet, useWindowDimensions } from 'react-native';
import MaterialIcons from 'react-native-vector-icons/MaterialIcons';
import FontFamily from '../../../../../constants/fonts';
import { DocColors } from './tokens';

export interface DocumentModalAction {
  text: string;
  onPress: () => void;
  disabled?: boolean;
}

interface DocumentModalProps {
  visible: boolean;
  title: string;
  onClose: () => void;
  /** Rendered left of the primary action (e.g. Cancel / Close). */
  secondaryAction?: DocumentModalAction;
  primaryAction?: DocumentModalAction;
  /** Max modal width in px (mockup uses 960 for the completion form). */
  width?: number;
  children: React.ReactNode;
}

const DocumentModal: React.FC<DocumentModalProps> = ({
  visible,
  title,
  onClose,
  secondaryAction,
  primaryAction,
  width = 960,
  children,
}) => {
  const { height: windowHeight } = useWindowDimensions();
  const hasFooter = !!secondaryAction || !!primaryAction;

  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={onClose}>
      <View style={styles.scrim}>
        <Pressable style={StyleSheet.absoluteFill} onPress={onClose} accessibilityLabel="Close dialog" />
        <View style={[styles.modal, { maxWidth: width, maxHeight: windowHeight * 0.9 }]}>
          <View style={styles.head}>
            <Text style={styles.title} numberOfLines={2}>{title}</Text>
            <Pressable onPress={onClose} hitSlop={8} accessibilityLabel="Close">
              <MaterialIcons name="close" size={24} color={DocColors.textPrimary} />
            </Pressable>
          </View>

          <ScrollView style={styles.bodyScroll} contentContainerStyle={styles.body} keyboardShouldPersistTaps="handled">
            {children}
          </ScrollView>

          {hasFooter && (
            <View style={styles.foot}>
              {secondaryAction && (
                <Pressable
                  style={[styles.btn, styles.btnSecondary, secondaryAction.disabled && styles.btnDisabled]}
                  disabled={secondaryAction.disabled}
                  onPress={secondaryAction.onPress}
                >
                  <Text style={[styles.btnText, styles.btnSecondaryText]}>{secondaryAction.text}</Text>
                </Pressable>
              )}
              {primaryAction && (
                <Pressable
                  style={[styles.btn, styles.btnPrimary, primaryAction.disabled && styles.btnDisabled]}
                  disabled={primaryAction.disabled}
                  onPress={primaryAction.onPress}
                >
                  <Text style={[styles.btnText, styles.btnPrimaryText]}>{primaryAction.text}</Text>
                </Pressable>
              )}
            </View>
          )}
        </View>
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  scrim: {
    flex: 1,
    backgroundColor: DocColors.scrim,
    alignItems: 'center',
    justifyContent: 'center',
    padding: 24,
  },
  modal: {
    width: '100%',
    backgroundColor: DocColors.surface,
    borderRadius: 8,
    overflow: 'hidden',
    shadowColor: DocColors.modalShadow,
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.12,
    shadowRadius: 24,
    elevation: 8,
  },
  head: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 16,
    paddingHorizontal: 24,
    paddingVertical: 16,
    borderBottomWidth: 1,
    borderBottomColor: DocColors.gray25,
  },
  title: {
    flex: 1,
    fontFamily: FontFamily.bold,
    fontSize: 20,
    lineHeight: 28,
    color: DocColors.textPrimary,
  },
  bodyScroll: {
    flexShrink: 1,
  },
  body: {
    padding: 24,
    gap: 16,
  },
  foot: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    gap: 16,
    paddingHorizontal: 24,
    paddingVertical: 16,
    borderTopWidth: 1,
    borderTopColor: DocColors.gray25,
  },
  btn: {
    height: 40,
    paddingHorizontal: 24,
    borderRadius: 24,
    borderWidth: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  btnPrimary: {
    backgroundColor: DocColors.blue800,
    borderColor: DocColors.blue800,
  },
  btnSecondary: {
    backgroundColor: DocColors.surface,
    borderColor: DocColors.blue800,
  },
  btnDisabled: {
    opacity: 0.45,
  },
  btnText: {
    fontFamily: FontFamily.bold,
    fontSize: 16,
    lineHeight: 22,
  },
  btnPrimaryText: {
    color: DocColors.textOnDark,
  },
  btnSecondaryText: {
    color: DocColors.blue800,
  },
});

export default DocumentModal;
